/**
 * Pricing — the entire app's pricing page (/pricing).
 *
 * One page, three plans, covering BOTH audiences the app serves.
 * The cards are dealt on the table like the Welcome reaction stack —
 * hand-dropped tilts on desktop, a clean snap carousel on mobile:
 *
 *   Free     — the consumer dating product (publish a card, get rated,
 *              limited DMs). The quiet paper card: resting tilt, muted
 *              check rows, ghost CTA. No coral anywhere.
 *   Premium  — everything in Free, plus the full human-app unlock
 *              (unlimited DMs, notes, receipts, map, …). The featured
 *              card and the page's single coral moment: upright +
 *              lifted, sunrise wash, punched-out coral sticker, and
 *              glyph-disc perk rows mirroring the in-app PaywallSheet's
 *              PerksList 1:1 (same icons, same order).
 *   Ultra    — everything in Premium, plus the agent/developer platform
 *              (agent keys, REST API, MCP server, webhooks, OAuth). A
 *              terminal window echoing the /agents code idiom (chrome
 *              bar, #221610 paper, cream ink) with a paper sticker +
 *              paper CTA — deliberately no coral, so Premium keeps the
 *              single accent (DESIGN.md §0.1).
 *
 * No web checkout: subscriptions are sold in the iOS + Android apps
 * (RevenueCat). A signed-in click on either Subscribe button pops the
 * canonical PaywallSheet (trigger 'pricing') which explains web
 * payments aren't ready and hands off to the stores. Subscribing needs
 * an account, so a signed-out click goes through
 * /auth/signin?returnTo=/pricing first and lands back here right after
 * auth. Web billing (CCBill/Segpay) ships dark and is not referenced
 * here.
 *
 * Prices are hardcoded client-side on purpose — the same convention as
 * the mobile PaywallSheet's DISPLAY_PRICES: the store products are the
 * source of truth and must be updated together. Ultra follows the same
 * curve as Premium (monthly = 3× weekly, yearly = 12× monthly less the
 * ~22% annual saving).
 *
 * Layout: a three-column grid on desktop; on mobile (≤980px) the grid
 * becomes a side-scrollable snap carousel (same pattern as the app's
 * VideosRow) with edge-bleed, center-snapped cards, dot pagination,
 * and Premium centered on first paint.
 *
 * Public route rendered inside PublicShell (humans nav + footer); the
 * agents-surface navs (Agents/AgentDocs/AgentApiRef) also link here.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api as convexApi } from '../../../convex/functions/_generated/api';
import { Icons, type IconKey } from '@/components/Icons';
import { requestPaywall } from '@/lib/paywall';
import './Pricing.css';

type Period = 'weekly' | 'monthly' | 'yearly';

const PERIODS: ReadonlyArray<{ id: Period; label: string; save?: string }> = [
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Yearly', save: 'Save 22%' },
];

/** Per-plan, per-period price display. Keep in sync with the store
 *  products + the mobile PaywallSheet DISPLAY_PRICES. */
const PRICES: Record<'premium' | 'ultra', Record<Period, { amount: string; per: string }>> = {
  premium: {
    weekly: { amount: '$9.99', per: 'per week' },
    monthly: { amount: '$29.99', per: 'per month' },
    yearly: { amount: '$279.99', per: 'per year' },
  },
  ultra: {
    weekly: { amount: '$13.99', per: 'per week' },
    monthly: { amount: '$41.99', per: 'per month' },
    yearly: { amount: '$389.99', per: 'per year' },
  },
};

const FREE_FEATURES: ReadonlyArray<string> = [
  'Publish your profile card',
  'Share your link anywhere',
  'Collect anonymous yes / no ratings',
  'Rate 50 profiles a day',
  '10 keeper spots',
  'Up to 10 messages per conversation',
];

/** Premium perks — same icons, same order as the PaywallSheet's
 *  BENEFITS (which mirrors the app's PremiumPerks) so the pitch is
 *  identical on every surface that sells Premium. */
const PREMIUM_FEATURES: ReadonlyArray<{ icon: IconKey; label: string }> = [
  { icon: 'chat', label: 'Unlimited messages, every conversation' },
  { icon: 'lock', label: 'Read every inbound message' },
  { icon: 'star', label: "See who's into you — rating notes" },
  { icon: 'eye', label: 'Read receipts on your messages' },
  { icon: 'clock', label: 'Disappearing messages' },
  { icon: 'bookmark', label: 'Unlimited keepers' },
  { icon: 'send', label: 'Unlimited DM threads' },
  { icon: 'undo', label: 'Undo any swipe' },
  { icon: 'compass', label: 'Live nearby map' },
];

const ULTRA_FEATURES: ReadonlyArray<{ icon: IconKey; label: string }> = [
  { icon: 'spark', label: 'Mint agent API keys' },
  { icon: 'gear', label: 'Full REST API access (/agent/v1)' },
  { icon: 'feed', label: 'MCP server — plug in your agent' },
  { icon: 'bell', label: 'Webhook events: ratings, DMs, matches' },
  { icon: 'user', label: 'OAuth “Connect with WYDM” delegation' },
];

/** Carousel order — must match the order of the plan <section>s. */
const PLAN_NAMES: ReadonlyArray<string> = ['Free', 'Premium', 'Ultra'];

/** Index of the card the mobile carousel centers on first paint —
 *  Premium, the plan we sell to humans. */
const DEFAULT_PLAN_INDEX = 1;

type EffectiveTier = 'free' | 'premium' | 'ultra';

export function PricingPage() {
  const [period, setPeriod] = useState<Period>('monthly');
  // Null when signed out — the current-plan badges just stay hidden.
  const me = useQuery(convexApi.users.me, {});
  const currentTier: EffectiveTier =
    me?.tier === 'ultra' ? 'ultra' : me?.tier === 'premium' ? 'premium' : 'free';
  const signedIn = me != null;
  // Subscribing requires an account. Signed-out clicks route through
  // sign-in and return here after auth, where a second click pops the
  // store-handoff sheet. (SignIn also auto-bounces already-authed users
  // to returnTo, which covers the brief window where `me` is still
  // loading.)
  const subscribeHref = `/auth/signin?returnTo=${encodeURIComponent('/pricing')}`;

  // ── Mobile plan carousel ──────────────────────────────────────────
  // ≤980px the .plans grid becomes a snap-scrolling flex row (CSS).
  // Here we only (a) center the Premium card on first paint so both
  // neighbors peek and signal scrollability, and (b) track the
  // centered card to drive the dot pagination. On desktop the row
  // never scrolls, so the dots stay hidden (CSS) and this is inert.
  const plansRef = useRef<HTMLDivElement>(null);
  const [activePlan, setActivePlan] = useState(DEFAULT_PLAN_INDEX);
  const centeredOnce = useRef(false);

  useLayoutEffect(() => {
    if (centeredOnce.current) return;
    centeredOnce.current = true;
    const scroller = plansRef.current;
    if (!scroller) return;
    // jsdom (unit tests) has no matchMedia — treat its absence as
    // "not mobile" and skip the initial centering.
    if (typeof window.matchMedia !== 'function') return;
    if (!window.matchMedia('(max-width: 980px)').matches) return;
    const target = scroller.children[DEFAULT_PLAN_INDEX] as HTMLElement | undefined;
    if (target) {
      scroller.scrollLeft = target.offsetLeft - (scroller.clientWidth - target.clientWidth) / 2;
    }
  }, []);

  const handlePlansScroll = () => {
    const scroller = plansRef.current;
    if (!scroller) return;
    const center = scroller.scrollLeft + scroller.clientWidth / 2;
    let best = DEFAULT_PLAN_INDEX;
    let bestDist = Infinity;
    Array.from(scroller.children).forEach((child, i) => {
      const el = child as HTMLElement;
      const dist = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    setActivePlan(best);
  };

  const scrollToPlan = (index: number) => {
    const scroller = plansRef.current;
    const target = scroller?.children[index] as HTMLElement | undefined;
    if (!scroller || !target) return;
    scroller.scrollTo({
      left: target.offsetLeft - (scroller.clientWidth - target.clientWidth) / 2,
      behavior: 'smooth',
    });
  };

  return (
    <div className="lp pricing" style={{ background: 'var(--bg)' }}>
      <div className="wrap pricing-wrap">
        {/* The design has no visible headline; this gives the page the
            one <h1> search engines and screen readers look for. */}
        <h1 className="visually-hidden">WYDM pricing: Free, Premium and Ultra plans</h1>
        {/* ── BILLING-PERIOD TOGGLE — switches the paid cards' prices ── */}
        <div style={{ textAlign: 'center' }}>
          <div className="period-toggle" role="tablist" aria-label="Billing period">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={period === p.id}
                className={`period-tab${period === p.id ? ' active' : ''}`}
                onClick={() => setPeriod(p.id)}
              >
                {p.label}
                {p.save && <span className="save">{p.save}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* ── PLAN CARDS — grid on desktop, snap carousel on mobile ── */}
        <div className="plans" ref={plansRef} onScroll={handlePlansScroll}>
          {/* Free — the quiet paper card */}
          <section className="plan plan-free" aria-label="Free plan">
            <div className="plan-headrow">
              <div className="plan-name">Free</div>
              {signedIn && currentTier === 'free' && <span className="plan-current">Current plan</span>}
            </div>
            <div className="plan-price">
              <span className="plan-amount">$0</span>
              <span className="plan-per">forever</span>
            </div>
            <div className="plan-save-row" />
            <p className="plan-tagline">Put your card out there and see what comes back.</p>
            <ul className="plan-feats">
              {FREE_FEATURES.map((f) => (
                <li key={f}>
                  <Icons.check className="feat-check" size={15} stroke={2.2} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="plan-cta">
              {/* Signed-in users already have an account — straight to
                  the builder; signed-out users sign up first (the
                  signup flow lands on /builder anyway). */}
              <Link to={signedIn ? '/builder' : '/auth/signup'} className="btn btn-lg btn-ghost">
                Build my card
              </Link>
            </div>
          </section>

          {/* Premium — the featured card, and the only coral moment */}
          <section className="plan plan-premium" aria-label="Premium plan">
            <span className="plan-sticker">Most popular</span>
            <div className="plan-headrow">
              <div className="plan-name">Premium</div>
              {currentTier === 'premium' && <span className="plan-current">Current plan</span>}
            </div>
            <div className="plan-price">
              <span className="plan-amount">{PRICES.premium[period].amount}</span>
              <span className="plan-per">{PRICES.premium[period].per}</span>
            </div>
            <div className="plan-save-row">
              {period === 'yearly' && <span className="plan-save">Save 22% vs monthly</span>}
            </div>
            <p className="plan-tagline">The full dating product, uncapped.</p>
            <ul className="plan-feats">
              <li className="feat-inherit">Everything in Free, plus:</li>
              {PREMIUM_FEATURES.map(({ icon, label }) => {
                const Glyph = Icons[icon];
                return (
                  <li key={label}>
                    <span className="feat-glyph" aria-hidden="true">
                      <Glyph size={14} />
                    </span>
                    <span>{label}</span>
                  </li>
                );
              })}
            </ul>
            <div className="plan-cta">
              {currentTier === 'premium' ? (
                <span className="plan-cta-note">This is your plan — manage it in the app.</span>
              ) : signedIn ? (
                <>
                  <button
                    type="button"
                    className="btn btn-coral btn-lg"
                    onClick={() => requestPaywall({ trigger: 'pricing' })}
                  >
                    Subscribe
                  </button>
                  <span className="plan-cta-note">iOS + Android · cancel anytime</span>
                </>
              ) : (
                <>
                  <Link to={subscribeHref} className="btn btn-coral btn-lg">
                    Subscribe
                  </Link>
                  <span className="plan-cta-note">iOS + Android · cancel anytime</span>
                </>
              )}
            </div>
          </section>

          {/* Ultra — a terminal window on the table, for agents */}
          <section className="plan plan-ultra" aria-label="Ultra plan">
            <span className="plan-sticker">For agents</span>
            <div className="plan-chrome" aria-hidden="true">
              <span className="d" />
              <span className="d" />
              <span className="d" />
              <span className="t">~/agent-platform</span>
            </div>
            <div className="plan-headrow">
              <div className="plan-name">Ultra</div>
              {currentTier === 'ultra' && <span className="plan-current">Current plan</span>}
            </div>
            <div className="plan-price">
              <span className="plan-amount">{PRICES.ultra[period].amount}</span>
              <span className="plan-per">{PRICES.ultra[period].per}</span>
            </div>
            <div className="plan-save-row">
              {period === 'yearly' && <span className="plan-save">Save 22% vs monthly</span>}
            </div>
            <p className="plan-tagline">Premium, plus the agent/developer platform.</p>
            <ul className="plan-feats">
              <li className="feat-inherit">Everything in Premium, plus:</li>
              {ULTRA_FEATURES.map(({ icon, label }) => {
                const Glyph = Icons[icon];
                return (
                  <li key={label}>
                    <span className="feat-glyph" aria-hidden="true">
                      <Glyph size={14} />
                    </span>
                    <span>{label}</span>
                  </li>
                );
              })}
            </ul>
            <div className="plan-cta">
              {currentTier === 'ultra' ? (
                <span className="plan-cta-note">This is your plan — manage it in the app.</span>
              ) : signedIn ? (
                <>
                  <button
                    type="button"
                    className="btn btn-paper btn-lg"
                    onClick={() => requestPaywall({ trigger: 'pricing' })}
                  >
                    Subscribe
                  </button>
                  <span className="plan-cta-note">iOS + Android · cancel anytime</span>
                </>
              ) : (
                <>
                  <Link to={subscribeHref} className="btn btn-paper btn-lg">
                    Subscribe
                  </Link>
                  <span className="plan-cta-note">iOS + Android · cancel anytime</span>
                </>
              )}
            </div>
          </section>
        </div>

        {/* Carousel pagination — mobile only (display:none on desktop) */}
        <div className="plans-dots" role="group" aria-label="Browse plans">
          {PLAN_NAMES.map((name, i) => (
            <button
              key={name}
              type="button"
              className={`plans-dot${activePlan === i ? ' active' : ''}`}
              aria-label={`Show the ${name} plan`}
              aria-current={activePlan === i}
              onClick={() => scrollToPlan(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
